import { CAPACITY_UTILIZATION } from 'constants/dashboardConstants';

import { getDiffDays, getRequestFormatDate, getWorkingDiffDays, maxDate, minDate } from './date';
import { accumulator } from './helpers';
import { getCalendarWithWorkHours } from './timelineUtilities';

const extractValue = (field, key = 'id') =>
  field.filter(option => option.value).map(option => option[key]);

export const filtersToParamsPeople = ({ skills, departments, startDate, endDate, ...filters }) => ({
  ...filters,
  skillIds: extractValue(skills),
  departmentIds: extractValue(departments),
  seniorities: extractValue(filters.seniorities, 'key'),
  startDate: getRequestFormatDate(startDate),
  endDate: getRequestFormatDate(endDate)
});

export const filtersToParamsReports = ({
  skills,
  departments,
  roles,
  startDate,
  endDate,
  ...filters
}) => ({
  ...filters,
  skillIds: extractValue(skills),
  departmentIds: extractValue(departments),
  roleIds: extractValue(roles),
  startDate: getRequestFormatDate(startDate),
  endDate: getRequestFormatDate(endDate)
});

export const filtersToParamsAssignment = ({ startDate, endDate, ...filters }) => ({
  ...filters,
  startDate: getRequestFormatDate(startDate),
  endDate: getRequestFormatDate(endDate)
});

export const RevenueGraphData = data =>
  data?.map(item => {
    const { revenue, percentage } = item;
    return {
      ...item,
      labelValue: {
        revenue,
        percentage
      }
    };
  });

export const getResourceAvailability = (
  assignments,
  workHours,
  availableWorkHours,
  startDatePeriod,
  endDatePeriod
) => {
  const calendar = getCalendarWithWorkHours(assignments, startDatePeriod, endDatePeriod);
  const assignmentsWorkHours = calendar.reduce((acc, calendarWorkHours) => acc + calendarWorkHours);
  const totalDays = getDiffDays(startDatePeriod, endDatePeriod) + 1;
  const totalCapacity = totalDays * (availableWorkHours || workHours);

  const availableHours = totalCapacity - assignmentsWorkHours;

  return (availableHours * 100) / totalCapacity;
};

export const convertResourcesDataToTableFormat = (resources, monthFirstDay, monthLastDay) =>
  resources?.map(
    ({
      firstName,
      lastName,
      fullName,
      role,
      assignments,
      timeOffs,
      workHours,
      availableWorkHours,
      avatarUrl,
      hireDate,
      exitDate,
      ...rest
    }) => {
      const getFullName =
        fullName ?? (lastName ? `${firstName ? `${firstName} ` : ''}${lastName || ''}` : 'N/A');

      return {
        ...rest,
        mainInfo: {
          role,
          fullName: getFullName,
          avatarUrl
        },
        assignments,
        availableWorkHours,
        workHours,
        availability: { assignments, timeOffs, workHours, availableWorkHours, hireDate, exitDate },
        availablePercentage: getResourceAvailability(
          assignments,
          workHours,
          availableWorkHours,
          monthFirstDay,
          monthLastDay
        )
      };
    }
  );

export const filterBenchList = (rows, activeFilters, hidePending, highlighted) => {
  if (!rows?.length) return [];

  const filterKeys = ['departments', 'roles', 'skills', 'availability'];
  const [departments, roles, skills] = filterKeys.map(key =>
    activeFilters.filter(({ field }) => field === key)?.map(({ label }) => label)
  );

  const availabilities = activeFilters
    .filter(({ field }) => field === 'availability')
    ?.map(({ key }) => key);

  let filteredRows = rows;

  if (departments.length) {
    filteredRows = filteredRows.filter(({ department }) => departments.includes(department));
  }

  if (roles.length) {
    filteredRows = filteredRows.filter(({ mainInfo }) => roles.includes(mainInfo?.role));
  }

  if (skills.length) {
    filteredRows = filteredRows.filter(row =>
      skills.some(skill =>
        highlighted
          ? row.mainSkills?.includes(skill)
          : row.mainSkills?.includes(skill) || row.skills?.includes(skill)
      )
    );
  }

  if (availabilities.length) {
    filteredRows = filteredRows.filter(({ availablePercentage }) =>
      availabilities.some(availability => {
        const [rangeStart, rangeEnd] = availability.split('-');

        return availablePercentage <= rangeEnd && availablePercentage >= rangeStart;
      })
    );
  }

  return hidePending ? filteredRows.filter(row => !row.hasPendingAssignments) : filteredRows;
};

export const getOverviewCurrentDayData = data => {
  if (!data) return [];
  return [
    {
      label: CAPACITY_UTILIZATION.demand.intl,
      value: data[CAPACITY_UTILIZATION.demand.key],
      color: CAPACITY_UTILIZATION.demand.stroke
    },
    {
      label: CAPACITY_UTILIZATION.scheduled.intl,
      value: data[CAPACITY_UTILIZATION.scheduled.key],
      color: CAPACITY_UTILIZATION.scheduled.stroke
    },
    {
      label: CAPACITY_UTILIZATION.billingCapacity.intl,
      value: data[CAPACITY_UTILIZATION.billingCapacity.key],
      color: CAPACITY_UTILIZATION.billingCapacity.stroke
    }
  ];
};

export const getResourcesAvailability = (resources, startDatePeriod, endDatePeriod) => {
  const benchHoursArray = [];

  const daysOfWorkInPeriod = (possibleStartDate, possibleEndDate) => {
    const startDate = maxDate(possibleStartDate, startDatePeriod);
    const endDate = minDate(possibleEndDate, endDatePeriod);
    return getWorkingDiffDays(startDate, endDate);
  };

  resources.map(resource => {
    const totalPersonHours =
      resource?.availableWorkHours * daysOfWorkInPeriod(resource?.hireDate, resource?.exitDate);
    const allocationHoursArray = resource?.assignments.map(assignment => {
      if (!assignment.support && !assignment.pending) {
        return (
          assignment?.workHours * daysOfWorkInPeriod(assignment?.startDate, assignment?.endDate)
        );
      }
      return 0;
    });
    const allocationHours = accumulator(allocationHoursArray);
    const benchHours = totalPersonHours - allocationHours;

    benchHoursArray.push(benchHours);
  });

  const availableHours = accumulator(benchHoursArray);
  const workingDaysHours = daysOfWorkInPeriod(startDatePeriod, endDatePeriod) * 7.5;

  return Math.round(availableHours / workingDaysHours);
};
